import type { ApiErrorBody, ApiSuccess } from '../../../shared/types';
import { API_BASE, APP_NAME, STORAGE_KEY_API } from '../config';

export class ApiError extends Error {
  readonly existing_lead_id?: string;
  readonly status: number;
  readonly code: string;
  readonly details?: { path: string; message: string }[];

  constructor(status: number, code: string, message: string, details?: { path: string; message: string }[], existing_lead_id?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.existing_lead_id = existing_lead_id;
  }
}

/** fetch() itself failed: offline, DNS, CORS, timeout. Never a server verdict. */
export class NetworkError extends Error {
  constructor(message = 'Network unavailable') {
    super(message);
    this.name = 'NetworkError';
  }
}

export const getApiKey = (): string | null => localStorage.getItem(STORAGE_KEY_API);
export const setApiKey = (key: string): void => localStorage.setItem(STORAGE_KEY_API, key);
export const clearApiKey = (): void => localStorage.removeItem(STORAGE_KEY_API);

export const UNAUTHORIZED_EVENT = 'sb:unauthorized';

const parseEnvelope = async (res: Response): Promise<unknown> => {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
};

export interface RequestOptions {
  /** Override the stored key (login screen probe). */
  apiKey?: string;
  signal?: AbortSignal;
  cache?: RequestCache;
}

/**
 * Call the Worker API. Resolves with `data` on 2xx, throws ApiError on a server verdict (4xx/5xx),
 * NetworkError when the request never got an answer. A 401 also broadcasts UNAUTHORIZED_EVENT.
 */
export const apiFetch = async <T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> => {
  const key = opts.apiKey ?? getApiKey() ?? '';
  const headers: Record<string, string> = { authorization: `Bearer ${key}` };
  // a file goes as it is (a city list upload, D61); anything else as JSON
  const raw = body instanceof Blob;
  if (body !== undefined) headers['content-type'] = raw ? 'application/octet-stream' : 'application/json';
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      cache: opts.cache,
      headers,
      body: body === undefined ? undefined : raw ? body : JSON.stringify(body),
      signal: opts.signal ?? AbortSignal.timeout(20_000),
    });
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : 'Network unavailable');
  }
  const payload = (await parseEnvelope(res)) as ApiSuccess<T> | ApiErrorBody | undefined;
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    // Guard against a 2xx that is not our API (e.g. a misconfigured VITE_API_URL serving HTML):
    // treating it as success could wipe the local mirror with garbage.
    if (!payload || typeof payload !== 'object' || (payload as { success?: unknown }).success !== true) {
      throw new ApiError(res.status, 'BAD_RESPONSE', `Response is not a ${APP_NAME} API envelope`);
    }
    return (payload as ApiSuccess<T>).data;
  }
  if (res.status === 401 && !opts.apiKey) window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  const error = payload && 'error' in payload ? payload.error : undefined;
  throw new ApiError(
    res.status,
    error?.code ?? 'HTTP_ERROR',
    error?.message ?? `HTTP ${res.status}`,
    Array.isArray(error?.details) ? error.details : undefined,
    error?.existing_lead_id,
  );
};

/** Login probe: true = key accepted, false = rejected. Throws NetworkError when offline. */
export const checkApiKey = async (apiKey: string): Promise<boolean> => {
  try {
    await apiFetch('GET', '/api/auth/check', undefined, { apiKey });
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return false;
    throw err;
  }
};
